'use client'
import { createMenu } from '@gluestack-ui/core/menu/creator'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { AnimatePresence, Motion, MotionComponentProps } from '@legendapp/motion'
import { cssInterop } from 'nativewind'
import React from 'react'
import { Platform, Pressable, ScrollView, Text, useWindowDimensions, View, ViewStyle } from 'react-native'

type IMotionViewProps = React.ComponentProps<typeof View> &
  MotionComponentProps<typeof View, ViewStyle, unknown, unknown, unknown>

const MotionView = Motion.View as React.ComponentType<IMotionViewProps & React.RefAttributes<View>>

const menuStyle = tva({
  base: 'rounded-2xl bg-background-0 border border-white dark:border-outline-400 p-1 shadow-hard-2',
})

const menuItemStyle = tva({
  base: 'min-w-[200px] p-3 flex-row items-center rounded-xl data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100 data-[focus=true]:bg-background-50 data-[focus=true]:web:outline-none data-[focus=true]:web:outline-0 data-[disabled=true]:opacity-40 data-[disabled=true]:web:cursor-not-allowed data-[focus-visible=true]:web:outline-2 data-[focus-visible=true]:web:outline-primary-700 data-[focus-visible=true]:web:outline data-[focus-visible=true]:web:cursor-pointer data-[disabled=true]:data-[focus=true]:bg-transparent',
})

const menuBackdropStyle = tva({
  base: 'absolute top-0 bottom-0 left-0 right-0 web:cursor-default',
  // add this classnames if you want to give background color to backdrop
  // opacity-50 bg-background-500,
})

const menuSeparatorStyle = tva({
  base: 'bg-background-200 h-px w-full',
})

const menuItemLabelStyle = tva({
  base: 'text-typography-700 font-normal font-body',

  variants: {
    isTruncated: {
      true: 'web:truncate',
    },
    bold: {
      true: 'font-bold',
    },
    underline: {
      true: 'underline',
    },
    strikeThrough: {
      true: 'line-through',
    },
    size: {
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
    },
    sub: {
      true: 'text-xs',
    },
    italic: {
      true: 'italic',
    },
    highlight: {
      true: 'bg-yellow-500',
    },
  },
})

const BackdropPressable = React.forwardRef<
  React.ComponentRef<typeof Pressable>,
  React.ComponentPropsWithoutRef<typeof Pressable> & VariantProps<typeof menuBackdropStyle>
>(function BackdropPressable({ className, ...props }, ref) {
  return (
    <Pressable
      ref={ref}
      className={menuBackdropStyle({
        class: className,
      })}
      {...props}
    />
  )
})

type IMenuItemProps = VariantProps<typeof menuItemStyle> & {
  className?: string
} & React.ComponentPropsWithoutRef<typeof Pressable>

const Item = React.forwardRef<React.ComponentRef<typeof Pressable>, IMenuItemProps>(function Item(
  { className, ...props },
  ref
) {
  return (
    <Pressable
      ref={ref}
      className={menuItemStyle({
        class: className,
      })}
      {...props}
    />
  )
})

const Separator = React.forwardRef<
  React.ComponentRef<typeof View>,
  React.ComponentPropsWithoutRef<typeof View> & VariantProps<typeof menuSeparatorStyle>
>(function Separator({ className, ...props }, ref) {
  return <View ref={ref} className={menuSeparatorStyle({ class: className })} {...props} />
})
// Android/web: long menus (e.g. template tag filter) otherwise overflow the viewport with no way to
// scroll. Pin ScrollView height to content (capped at 60% window height) so the panel shrink-wraps —
// on Android a plain ScrollView would also expand to the overlay maxHeight, stretching the panel and
// blocking touches.
const ScrollableMotionView = React.forwardRef<React.ComponentRef<typeof View>, IMotionViewProps>(
  function ScrollableMotionView({ children, style, ...props }, ref) {
    const { height: windowHeight } = useWindowDimensions()
    const maxMenuHeight = windowHeight * 0.6
    const [contentHeight, setContentHeight] = React.useState<number | null>(null)

    const scrollStyle = React.useMemo(() => {
      // Web ScrollView is CSS overflow-y auto, so maxHeight caps and scrolls without
      // waiting for content measurement (which Android needs to shrink-wrap the panel)
      if (Platform.OS === 'web') {
        return { maxHeight: maxMenuHeight, flexGrow: 0 as const }
      }
      if (contentHeight == null) {
        return { alignSelf: 'flex-start' as const, flexGrow: 0 as const }
      }
      return {
        height: Math.min(contentHeight, maxMenuHeight),
        flexGrow: 0 as const,
      }
    }, [contentHeight, maxMenuHeight])

    return (
      <MotionView ref={ref} {...props} style={[{ alignSelf: 'flex-start' }, style]}>
        <ScrollView
          nestedScrollEnabled
          showsVerticalScrollIndicator={contentHeight != null && contentHeight > maxMenuHeight - 1}
          contentContainerStyle={{ flexGrow: 0 }}
          style={scrollStyle}
          onContentSizeChange={(_w, h) => setContentHeight(h)}
        >
          {children}
        </ScrollView>
      </MotionView>
    )
  }
)

cssInterop(ScrollableMotionView, { className: 'style' })

export const UIMenu = createMenu({
  Root: Platform.OS === 'ios' ? MotionView : ScrollableMotionView,
  Item: Item,
  Label: Text,
  Backdrop: BackdropPressable,
  AnimatePresence: AnimatePresence,
  Separator: Separator,
})

cssInterop(MotionView, { className: 'style' })

type IMenuProps = React.ComponentProps<typeof UIMenu> & VariantProps<typeof menuStyle> & { className?: string }

const menuRootElevation: ViewStyle | undefined =
  Platform.OS === 'android'
    ? {
        elevation: 40,
        shadowColor: '#262626',
        shadowOpacity: 0.38,
        shadowOffset: { width: 0, height: 3 },
        shadowRadius: 10,
      }
    : undefined

type IMenuItemLabelProps = React.ComponentProps<typeof UIMenu.ItemLabel> &
  VariantProps<typeof menuItemLabelStyle> & { className?: string }

const Menu = React.forwardRef<React.ComponentRef<typeof UIMenu>, IMenuProps>(function Menu(
  { className, style, ...props },
  ref
) {
  return (
    <UIMenu
      ref={ref}
      initial={{
        opacity: 0,
        scale: 0.8,
      }}
      animate={{
        opacity: 1,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        scale: 0.8,
      }}
      transition={{
        type: 'timing',
        duration: 100,
      }}
      className={menuStyle({
        class: className,
      })}
      style={[menuRootElevation, style]}
      {...props}
    />
  )
})

const MenuItem = UIMenu.Item

const MenuItemLabel = React.forwardRef<React.ComponentRef<typeof UIMenu.ItemLabel>, IMenuItemLabelProps>(
  function MenuItemLabel(
    { className, isTruncated, bold, underline, strikeThrough, size = 'md', sub, italic, highlight, ...props },
    ref
  ) {
    return (
      <UIMenu.ItemLabel
        ref={ref}
        className={menuItemLabelStyle({
          isTruncated,
          bold,
          underline,
          strikeThrough,
          size,
          sub,
          italic,
          highlight,
          class: className,
        } as NonNullable<Parameters<typeof menuItemLabelStyle>[0]>)}
        {...props}
      />
    )
  }
)

const MenuSeparator = UIMenu.Separator

Menu.displayName = 'Menu'
MenuItem.displayName = 'MenuItem'
MenuItemLabel.displayName = 'MenuItemLabel'
MenuSeparator.displayName = 'MenuSeparator'
export { Menu, MenuItem, MenuItemLabel, MenuSeparator }
